import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Edit, Eye, Flag, Share2, ThumbsUp, Trash2, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Post } from '@/types/board';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deletePost, toggleLike } from '@/services/postService';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useEffect } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface PostDetailHeaderProps {
  post: Post;
  liked: boolean;
  setLiked: (value: boolean) => void;
  formatDate: (date: string) => string;
}

export const PostDetailHeader = ({ post, liked, setLiked, formatDate }: PostDetailHeaderProps) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isWeeLinkPost = post.category === 'Weee Link 공유';
  const isAdminUser = user?.email === 'ilwoonge@gmail.com';
  const canDeletePost = isAdminUser || (user && post.author_id === user.id);
  const isNotice = post.category === '공지사항';
  const isLocalNews = post.category === '로컬뉴스';
  const commentCount = post.commentCount || post.comment_count || 0;

  useEffect(() => {
    if (isWeeLinkPost) {
      window.location.href = post.content;
    }
  }, [post, isWeeLinkPost]);

  const likeMutation = useMutation({
    mutationFn: () => {
      return toggleLike(post.id, liked ? post.like_count - 1 : post.like_count + 1);
    },
    onSuccess: () => {
      setLiked(!liked);
      queryClient.invalidateQueries({ queryKey: ['post', post.id] });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "오류가 발생했습니다",
        description: "좋아요를 처리하는데 실패했습니다."
      });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      return deletePost(post.id);
    },
    onSuccess: () => {
      toast({
        title: "게시글이 삭제되었습니다",
      });
      navigate('/board');
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "오류가 발생했습니다",
        description: "게시글 삭제에 실패했습니다."
      });
    }
  });
  
  const handleLike = () => {
    if (!user) {
      toast({
        description: "좋아요를 누르려면 로그인이 필요합니다."
      });
      return;
    }
    likeMutation.mutate();
  };

  const isAuthor = user && post.author_id === user.id;

  return (
    <>
      <Button variant="ghost" asChild className="mb-4">
        <Link to="/board">
          <ArrowLeft className="mr-2 h-4 w-4" />
          게시판으로 돌아가기
        </Link>
      </Button>
      
      <Card className="mb-8">
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardDescription>
                <Link to={`/board?category=${post.category}`} className="text-primary hover:underline">
                  {post.category}
                </Link>
              </CardDescription>
              <CardTitle className="text-2xl mt-1">{post.title}</CardTitle>
            </div>
            
            {(isAuthor || isAdminUser) && (
              <div className="flex space-x-2">
                {isAuthor && (
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/board/edit/${post.id}`}>
                      <Edit className="h-4 w-4 mr-2" />
                      수정
                    </Link>
                  </Button>
                )}
                
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" size="sm">
                      <Trash2 className="h-4 w-4 mr-2" />
                      삭제
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>게시글을 삭제하시겠습니까?</AlertDialogTitle>
                      <AlertDialogDescription>
                        이 작업은 되돌릴 수 없습니다. 게시글을 삭제하면 모든 댓글도 함께 삭제됩니다.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>취소</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={() => deleteMutation.mutate()}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        삭제
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
          </div>
          
          <div className="flex flex-col mt-4">
            <div className="flex items-center text-muted-foreground">
              {!isNotice && !isLocalNews && post.author_name && (
                <div className="flex items-center gap-2 w-full">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={post.author_avatar} alt={post.author_name} />
                    <AvatarFallback>{post.author_name.charAt(0).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <p className="text-sm font-medium text-foreground">{post.author_name}</p>
                </div>
              )}
            </div>
            <div className="flex items-center text-xs mt-2">
              <Calendar className="h-3 w-3 mr-1" />
              <span>{formatDate(post.created_at || post.createdAt)}</span>
              {!isWeeLinkPost && (
                <>
                  <span className="mx-2">•</span>
                  <Eye className="h-3 w-3 mr-1" />
                  <span>{post.view_count || post.viewCount || 0}</span>
                  
                  <span className="mx-2">•</span>
                  <MessageSquare className="h-3 w-3 mr-1" />
                  <span>{commentCount}</span>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {post.image_url && (
            <div className="mb-4 rounded-lg overflow-hidden">
              <img 
                src={post.image_url} 
                alt={post.title} 
                className="w-full max-h-96 object-contain"
                loading="eager"
              />
            </div>
          )}
          <div className="prose max-w-none">
            <div dangerouslySetInnerHTML={{ __html: post.content }} />
          </div>
        </CardContent>
        
        <CardFooter className="flex justify-between pt-4 border-t">
          <div className="flex space-x-2">
            {!isWeeLinkPost && (
              <Button 
                variant={liked ? "default" : "outline"} 
                size="sm" 
                onClick={handleLike}
                disabled={likeMutation.isPending}
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                {liked ? '좋아요 취소' : '좋아요'} ({post.like_count || post.likeCount || 0})
              </Button>
            )}
            <Button variant="outline" size="sm">
              <Share2 className="h-4 w-4 mr-2" />
              공유하기
            </Button>
          </div>
          <Button variant="ghost" size="sm">
            <Flag className="h-4 w-4 mr-2" />
            신고하기
          </Button>
        </CardFooter>
      </Card>
    </>
  );
};
